"use client";

// React & Nextjs imports
import React, { useState, useMemo } from "react";

// Third party imports
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    TooltipItem,
    ChartData,
} from "chart.js";
import { format } from "date-fns";

// Models & Constants
import { UsStock } from "@/app/interface/global";
import { CountryCurrency } from "@/app/interface/currency";

// Hooks & Utils & Contexts
import useMobile from "@/app/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useCulture } from "@/context/cultureProvider";
import {
    formatAmountToUS,
    formatAmountWithDecimal,
    formatCurrency,
    // getPureAmount,
    // parseAmount,
} from "@/app/utils/currency-formatter";

// UI Components
import { Button } from "@/components/ui/button";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Format date for chart labels
 */
export const formatChartDate = (date: Date): string => {
    return format(date, "MMM yyyy");
};

/**
 * Format price for display
 */
export const formatPrice = (price: number, cultureDetail: CountryCurrency): string => {
    return formatCurrency(price, cultureDetail.currencyCode, cultureDetail.locale);
};

const KeyFinancialTab = {
    RETURNS: "Returns",
    INCOME: "Income",
    BALANCE_SHEET: "Balance sheet",
} as const;

type KeyFinancialTabKey = keyof typeof KeyFinancialTab;
export type KeyFinancialTabValue = (typeof KeyFinancialTab)[KeyFinancialTabKey];

const optionsTab = {
    ANNUAL: "Annual",
    QUARTERLY: "Quarterly",
} as const;

type optionsTabValues = (typeof optionsTab)[keyof typeof optionsTab];

export interface Props {
    stockDetails: UsStock;
    annualChartData: ChartData<"bar", (number | false)[], string>;
    quarterlyChartData: ChartData<"bar", (number | false)[], string>;
    selectedYear: string;
    setSelectedYear: React.Dispatch<React.SetStateAction<string>>;
    activeTab: KeyFinancialTabValue;
}

const BarChartAnnualQuarterlyMobile: React.FC<Props> = ({
    stockDetails,
    annualChartData,
    quarterlyChartData,
    selectedYear,
    setSelectedYear,
    activeTab,
}) => {
    const financialData = stockDetails?.company_financials;
    const { cultureContext } = useCulture();
    const { isMobile, hasLoaded } = useMobile();

    // const [activeTab, setActiveTab] = useState<KeyFinancialTabValue>(KeyFinancialTab.INCOME);
    const [viewType, setViewType] = useState<optionsTabValues>(optionsTab.ANNUAL);

    const availableYears = useMemo(() => {
        if (activeTab === KeyFinancialTab.INCOME) {
            return [...new Set(financialData.income.quarters.map((item) => item.year.toString()))];
        } else if (activeTab === KeyFinancialTab.BALANCE_SHEET) {
            return [
                ...new Set(
                    financialData.balance_sheet.quarters.map((item) => item.year.toString()),
                ),
            ];
        }
        return [];
    }, [activeTab, financialData.income.quarters, financialData.balance_sheet.quarters]);

    const chartOptions: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: "bottom" as const,
                labels: {
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 12,
                    /* font: {
                        size: 12,
                        weight: "500" as const,
                    }, */
                    color: "#64748b",
                },
            },
            tooltip: {
                /* backgroundColor: "rgba(0, 0, 0, 0.8)",
                padding: 12,
                bodyFont: {
                    size: 12,
                }, */
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                padding: 12,
                titleFont: {
                    size: 12,
                    weight: 600 as unknown as "bold" | "normal" | "lighter" | "bolder",
                },
                bodyFont: {
                    size: 12,
                },
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    /* title(context) {
                        if (context[0]) {
                            const date = new Date(context[0].parsed.x as unknown as number);
                            return `${formatChartDate(date)} ${date && forecastDate && Number(date) === Number(forecastDate) ? "[Forecast]" : ""}`;
                        }
                        return "";
                    }, */
                    label(tooltipItem: TooltipItem<"bar">) {
                        if (activeTab === KeyFinancialTab.RETURNS) {
                            return ` ${tooltipItem.dataset.label ? tooltipItem.dataset.label + ":" : ""} ${tooltipItem.parsed.y}%`;
                        } else {
                            return ` ${tooltipItem.dataset.label ? tooltipItem.dataset.label + ":" : ""} ${formatAmountToUS(tooltipItem.parsed.y || 0, cultureContext.symbol)}`;
                        }
                    },
                },
            },
        },
        layout: {
            padding: {
                bottom: isMobile ? 0 : activeTab === KeyFinancialTab.RETURNS ? 0 : 45,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: "rgba(226, 232, 240, 0.5)",
                },
                ticks: {
                    color: "#64748b",
                    font: {
                        size: 11,
                    },
                    callback: function (value: unknown) {
                        return formatAmountWithDecimal(
                            formatAmountToUS(value as number, cultureContext.symbol),
                        );
                    },
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: "#64748b",
                    font: {
                        size: 11,
                    },
                },
            },
        },
    };

    return (
        <div className="mt-6 w-full max-w-full">
            <Card className="min-h-[358px] rounded-xl py-3">
                {hasLoaded && (
                    <>
                        <div className="relative mb-2 flex items-center justify-between border-0 border-[#0E0F171A] px-4 min-h-[37px]">
                            <h3 className="text-xl font-semibold">{activeTab}</h3>
                            {viewType === optionsTab.QUARTERLY && (
                                <Select value={selectedYear} onValueChange={setSelectedYear}>
                                    <SelectTrigger
                                        className="focus:outline-0! focus:ring-0! -mr-2 h-auto w-auto border-0 transition-all duration-200"
                                        disabled={viewType !== optionsTab.QUARTERLY}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="">
                                        {availableYears.map((year) => (
                                            <SelectItem key={year} value={year}>
                                                {year}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        <div className="mx-4 mb-4 flex h-12 items-center justify-center rounded-full bg-[#F4F4F7] p-[calc((36px-28px)/2)] text-base font-medium">
                            <Button
                                variant={viewType === optionsTab.ANNUAL ? "outline" : null}
                                size="sm"
                                onClick={() => setViewType(optionsTab.ANNUAL)}
                                className={cn(
                                    "h-10 w-1/2 rounded-full border-[#0E0F171A] xs:h-7 xs:w-[93px]",
                                    viewType === optionsTab.ANNUAL
                                        ? "shadow-sm shadow-[#1018280D]"
                                        : "text-[#9195A9]",
                                )}
                            >
                                {optionsTab.ANNUAL}
                            </Button>
                            <Button
                                variant={viewType === optionsTab.QUARTERLY ? "outline" : null}
                                size="sm"
                                onClick={() => setViewType(optionsTab.QUARTERLY)}
                                className={cn(
                                    "h-10 w-1/2 rounded-full border-[#0E0F171A] xs:h-7 xs:w-[93px]",
                                    viewType === optionsTab.QUARTERLY
                                        ? "shadow-sm shadow-[#1018280D]"
                                        : "text-[#9195A9]",
                                )}
                            >
                                {optionsTab.QUARTERLY}
                            </Button>
                        </div>

                        <div className="min-h-[280px] pl-2 transition-all duration-200 xs:h-96">
                            <Bar
                                data={
                                    viewType === optionsTab.ANNUAL
                                        ? annualChartData
                                        : quarterlyChartData
                                }
                                options={chartOptions}
                                width={40}
                                height={35}
                            />
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
};

export default BarChartAnnualQuarterlyMobile;
